import type { BasketCheckoutButtonMessageModel, BasketCheckoutButtonModel } from 'autogen/swagger/all';
import { ButtonWithIcon } from 'components/Button';
import QuickView, { useQuickView } from 'components/quickView';
import createQueue from 'modules/helpers/queue';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { useState, type ReactNode } from 'react';
import styled from 'styled-components';
import NoticeMessage from 'views/basket/checkoutMessages/messageComponents/NoticeMessage';
import PotentialLoginDiscountMessage from 'views/basket/checkoutMessages/messageComponents/PotentialLoginDiscountMessage';
import SamplesMessage from 'views/basket/checkoutMessages/messageComponents/SamplesMessage';
import SingleSampleMessage from 'views/basket/checkoutMessages/messageComponents/SingleSampleMessage';
import { removeHTMLTagsFromString } from 'modules/helpers/strings';
import Icon from 'components/Icon';

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    flex: 2;
`;

export type MessageWithQueueCallbacks = BasketCheckoutButtonMessageModel & {
    nextMessageInQueue: () => void;
    closeAndCancelQueue: () => void;
    isLastMessage: boolean;
};

function useCheckoutMessageQueue(messages, url) {
    const [activeMessage, setActiveMessage] = useState<MessageWithQueueCallbacks>();
    const { toggle } = useQuickView('checkoutMessages');

    const startMessageQueue = () => {
        const queueJobs = messages.map(
            (message: BasketCheckoutButtonMessageModel, i): (() => Promise<void>) =>
                (): Promise<void> =>
                    new Promise((resolve, reject): void => {
                        const eventLabel = message.messageContent
                            ? removeHTMLTagsFromString(message.messageContent)
                            : message.messageType;
                        const queueCallbacks = {
                            nextMessageInQueue: () => {
                                if (queue.queue.length > 1) {
                                    toggle(false);
                                    /*
                                        this implementation based on the old checkout messages
                                        doesn't have a good way to hook into the animation
                                        callback so ive used a timeout for now.
                                     */
                                    setTimeout(() => {
                                        resolve();
                                    }, 400);

                                    UAEventTracking({
                                        eventCategory: 'basket',
                                        eventAction: 'closeModal',
                                        eventLabel
                                    });
                                } else {
                                    window.location.href = url;
                                }
                            },
                            closeAndCancelQueue: () => {
                                queue.cancel();
                                toggle(false);
                                reject(new Error('Message Queue cancelled'));
                            }
                        };

                        const messageWithQueueCallbacks = { ...message, ...queueCallbacks, isLastMessage: i === 0 };
                        setActiveMessage(messageWithQueueCallbacks);
                        toggle(true);
                        UAEventTracking({
                            eventCategory: 'basket',
                            eventAction: 'openModal',
                            eventLabel
                        });
                    })
        );
        const queue = createQueue();
        queue.push(...queueJobs);
        queue.start();
    };

    return { activeMessage, startMessageQueue };
}

function MessageComponent({ message }: { message: MessageWithQueueCallbacks }): JSX.Element {
    switch (message.messageType) {
        case 'SamplesMessage':
            return <SamplesMessage message={message} />;
        case 'SingleSampleMessage':
            return <SingleSampleMessage message={message} />;
        case 'PotentialLoginDiscountMessage':
            return <PotentialLoginDiscountMessage message={message} />;
        case 'UnclaimedGiftMessage':
        default:
            return <NoticeMessage message={message} />;
    }
}

export default function BasketCheckoutMessagesButton({
    model,
    children
}: {
    model: BasketCheckoutButtonModel;
    children: ReactNode;
}) {
    const { isEnabled, linkAction, messages } = model;
    const { activeMessage, startMessageQueue } = useCheckoutMessageQueue(messages, linkAction.url);

    return (
        <>
            {activeMessage && (
                <QuickView quickViewId="checkoutMessages" onClose={() => activeMessage.closeAndCancelQueue()}>
                    <MessageComponent message={activeMessage} />
                </QuickView>
            )}
            <StyledButtonWithIcon
                disabled={!isEnabled}
                icon={<Icon type="ChevronRight" size={16} />}
                size="lg"
                iconProperties={{ position: 'right' }}
                onClick={startMessageQueue}
            >
                {children}
            </StyledButtonWithIcon>
        </>
    );
}
